import { Injectable } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';

export type Devices = MediaDeviceInfo[];

/**
 * Serwis warstwy sprzętowej obsługujący urządzenia multimedialne.
 * Monitoruje uprawnienia przeglądarki, wykrywa podłączone urządzenia i dynamicznie
 * reaguje na zmiany sprzętowe.
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  $devicesUpdated: Observable<Promise<Devices>>;

  private deviceBroadcast = new ReplaySubject<Promise<Devices>>();

  constructor() {
    if (navigator && navigator.mediaDevices) {
      navigator.mediaDevices.ondevicechange = (_: Event) => {
        this.deviceBroadcast.next(this.getDeviceOptions());
      }
    }

    this.$devicesUpdated = this.deviceBroadcast.asObservable();
    this.deviceBroadcast.next(this.getDeviceOptions());
  }

  private async isGrantedMediaPermissions() {
    if (navigator && navigator['permissions']) {
      try {
        const permissionName = 'camera' as PermissionName;
        const result = await navigator['permissions'].query({ name: permissionName });
        if (result) {
          if (result.state === 'granted') {
            return true;
          } else {
            const isGranted = await new Promise<boolean>(resolve => {
              result.onchange = (_: Event) => {
                const granted = _.target['state'] === 'granted';
                if (granted) {
                  resolve(true);
                }
              }
            });

            return isGranted;
          }
        }
      } catch (e) {
        // This is only currently supported in Chrome.
        // https://stackoverflow.com/a/53155894/2410379
        return true;
      }
    }

    return false;
  }

  private async getDeviceOptions(): Promise<Devices> {
    const isGranted = await this.isGrantedMediaPermissions();
    if (navigator && navigator.mediaDevices && isGranted) {
      let devices = await this.tryGetDevices();
      if (devices.every(d => !d.label)) {
        devices = await this.tryGetDevices();
      }

      return devices;
    }

    return null;
  }

  private async tryGetDevices() {
    const mediaDevices = await navigator.mediaDevices.enumerateDevices();

    let devices = [] as Devices;

    mediaDevices.forEach(device => {
      if (['audioinput', 'audiooutput', 'videoinput'].includes(device.kind)) {
        devices.push(device);
      }
    })

    return devices;
  }
}
